import React, { useEffect, useState, useCallback, useMemo } from 'react';
import SidePanel from '../../components/common/side-panel/SidePanel';
import './WellFilterPanel.scss';
import { ReactComponent as ChevronUp } from '../../images/chevron-up.svg';
import { ReactComponent as ChevronDown } from '../../images/chevron-down.svg';
import { useAppDispatch, useAppSelector } from '../../hooks/storeHooks';
import { useLocation } from 'react-router-dom';
import Modal from '../common/Modal/Modal';
import shutdownIcon from '../../images/shutdown.svg';
import Tooltip from '../common/tooltip/ToolTip';
import {
  setSelectedFilters,
  setSelectedFilterType,
  fetchBusinessUnits,
  fetchFields,
  fetchMethodsOfProduction,
  fetchPCWellDetailsThunk,
  fetchFilteredAssets,
} from './WellFilterSlice';
import WellGroupDropDown from './well-group-dropdown/WellGroupDropdown';
import {
  fetchFilteredWellSummary,
  fetchFieldProduction,
  fetchStatusCountsWithFilters,
  clearStatusValues,
  fetchRodpumpclassification,
  fetchAlarmsData,
  selectFetchDataSummary,
} from '../well-summary/WellSummaryDashboardSlice';
import { DashboardFilterArr } from '../../types/WellSummaryDashboardTypes';
import useConfig from '../../hooks/useConfig';
import { fetchPCAreasAPI, fetchPCBusinessUnitsAPI, fetchPCFieldsAPI } from '../../services/wellFilterService';
import { extractGroupNamesFromFilters, hasActiveRelevantFilters } from '../../utilities/GlobalFilterUtil';

interface WellFilterPanelProps {
  isWellFilterOpen: boolean;
  setIsWellFilterOpen: (open: boolean) => void;
}

type SectionFilter = { section: string; value: string; id?: number | string };
type FilterItem = string | { Name: string; Id: number };

const createDefaultState = () => ({
  selectedFilter: 'all' as string,
  selectedCheckboxes: [[], [], []] as FilterItem[][],
  openSections: [true, true, true],
  expandedSections: [false, false, false],
  isGroupsOpen: false,
  selectedGroups: [] as string[],
});
const getLabel = (item: any) => {
  if (item === null || item === undefined) return '';
  if (typeof item === 'string') return item.trim();
  return (item.displayName || item.label || item.name || item.value || '').toString().trim();
};
const arrToCleanArr = (arr: any[] = []) => (arr || []).map(getLabel).filter(Boolean);

const WellFilterPanel: React.FC<WellFilterPanelProps> = ({ isWellFilterOpen, setIsWellFilterOpen }) => {
  const defaultState = useMemo(() => createDefaultState(), []);
  const location = useLocation();

  const { enablePumpChecker } = useConfig();
  const isWellSummaryPage = location.pathname === '/';
  const isCustomViews = /^\/group\/customviews/i.test(location.pathname);
  const PCFilter = enablePumpChecker && location.pathname !== '/' && !isCustomViews;

  const filterSectionTitles = PCFilter
    ? ['Business unit', 'Area', 'Field']
    : ['Business unit', 'Field', 'Method of production'];

  const [panelState, setPanelState] = useState<{
    selectedFilter: string;
    selectedCheckboxes: FilterItem[][];
    openSections: boolean[];
    expandedSections: boolean[];
    isGroupsOpen: boolean;
    selectedGroups: string[];
  }>(defaultState);
  const [initialPanelState, setInitialPanelState] = useState<typeof panelState>(defaultState);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const { selectedFilter, openSections, selectedCheckboxes, expandedSections, isGroupsOpen, selectedGroups } =
    panelState;

  const dispatch = useAppDispatch();
  const selectedFilters = useAppSelector((state) =>
    PCFilter ? state.WellFiltersSlice.PCSelectedFilters : state.WellFiltersSlice.selectedFilters,
  );
  const selectedFilterType = useAppSelector((state) =>
    PCFilter ? state.WellFiltersSlice.PCSelectedFilterType : state.WellFiltersSlice.selectedFilterType,
  );
  const pcSelectedAssets = useAppSelector((state) => state.WellFiltersSlice.PCSelectedAssets);
  const businessUnitItems = useAppSelector((state) => state.WellFiltersSlice.businessUnits);
  const fieldItems = useAppSelector((state) => state.WellFiltersSlice.fields);
  const methodItems = useAppSelector((state) => state.WellFiltersSlice.methodsOfProduction);
  const assetGroups = useAppSelector((state) => state.assetGroups.groups) || [];
  const fetchDataSummary = useAppSelector(selectFetchDataSummary);
  const pcBusinessUnits = useAppSelector((state) => state.WellFiltersSlice.PCBusinessUnits);
  const pcFieldItems = useAppSelector((state) => state.WellFiltersSlice.PCFields);
  const pcAreaItems = useAppSelector((state) => state.WellFiltersSlice.PCAreas);

  const [resetClicked, setResetClicked] = useState(false);

  if (isCustomViews) {
    const { hasActiveFilters, selectedGroupNames } = useMemo(() => {
      const hasActive = hasActiveRelevantFilters(selectedFilters);
      const groupNames = extractGroupNamesFromFilters(selectedFilters);

      return { hasActiveFilters: hasActive, selectedGroupNames: groupNames };
    }, [selectedFilters]);

    useEffect(() => {
      if (hasActiveFilters) {
        dispatch(
          fetchFilteredAssets({
            groupNames: selectedGroupNames,
            selectedFilters,
          }),
        );
      }
    }, [hasActiveFilters, selectedGroupNames, selectedFilters, dispatch]);
  }

  useEffect(() => {
    if (enablePumpChecker) {
      dispatch(fetchPCBusinessUnitsAPI());
      dispatch(fetchPCFieldsAPI({}));
      dispatch(fetchPCAreasAPI());
    }
  }, [dispatch, enablePumpChecker]);

  useEffect(() => {
    dispatch(fetchBusinessUnits());
    dispatch(fetchFields());
    dispatch(fetchMethodsOfProduction());
  }, [dispatch]);

  const sectionItems = useMemo(
    () =>
      PCFilter
        ? [
          Array.isArray(pcBusinessUnits)
            ? pcBusinessUnits.map((bu) => ({ Name: bu.Name, Id: bu.BusinessUnitId }))
            : [],
          Array.isArray(pcAreaItems) ? pcAreaItems.map((area) => ({ Name: area.Name, Id: area.AreaId })) : [],
          Array.isArray(pcFieldItems) ? pcFieldItems.map((field) => ({ Name: field.Name, Id: field.FieldId })) : [],
        ]
        : [businessUnitItems, fieldItems, methodItems],
    [PCFilter, pcBusinessUnits, pcAreaItems, pcFieldItems, businessUnitItems, fieldItems, methodItems],
  );

  // Helper function to get all group names recursively
  const getAllGroupNames = (groups: any[]): string[] => {
    const result: string[] = [];
    const collect = (groupList: any[]) => {
      groupList.forEach((group) => {
        if (group.assetGroupName) result.push(group.assetGroupName);
        if (group.childGroups?.length) collect(group.childGroups);
      });
    };
    collect(groups);
    return result;
  };

  // Helper function to separate group filters and asset filters from other filters
  const separateFilters = useCallback(
    (filters: SectionFilter[]) => {
      const allGroupNames = getAllGroupNames(assetGroups);
      return filters.reduce(
        (acc, filter) => {
          const isGroupFilter =
            filter.section === 'Groups' ||
            (filter.section !== 'Method of production' &&
              filter.section !== 'Business unit' &&
              filter.section !== 'Field' &&
              filter.section !== 'Area' &&
              filter.section !== 'Assets' &&
              allGroupNames.includes(filter.value));

          const isAssetFilter = filter.section === 'Assets';

          if (isGroupFilter) {
            acc.groupFilters.push(filter.value);
          } else if (isAssetFilter) {
            acc.assetFilters.push(filter);
          } else {
            acc.otherFilters.push(filter);
          }
          return acc;
        },
        { groupFilters: [] as string[], assetFilters: [] as SectionFilter[], otherFilters: [] as SectionFilter[] },
      );
    },
    [assetGroups],
  );

  const separatedFilters = useMemo(() => separateFilters(selectedFilters), [separateFilters, selectedFilters]);

  useEffect(() => {
    if (!isWellFilterOpen) {
      setPanelState(defaultState);
      return;
    }
    const { groupFilters, assetFilters, otherFilters } = separatedFilters;

    if (isCustomViews) {
      const newSelectedCheckboxes = sectionItems.map((items, idx) => {
        const sectionTitle = filterSectionTitles[idx];
        const appliedForSection = otherFilters.filter((f) => f.section === sectionTitle);
        return (items as FilterItem[]).filter((item) => {
          if (typeof item === 'object') {
            return appliedForSection.some((f) => f.id === item.Id && f.value === item.Name);
          }
          return appliedForSection.some((f) => f.value === item);
        });
      });

      const currentState = {
        ...defaultState,
        selectedFilter: selectedFilterType,
        selectedCheckboxes: newSelectedCheckboxes,
        selectedGroups: groupFilters,
        isGroupsOpen: selectedFilterType === 'groups' && groupFilters.length > 0,
      };
      setInitialPanelState(currentState);
      setPanelState(currentState);
      return;
    }

    if (PCFilter) {
      const appliedBUs = otherFilters
        .filter((f) => f.section === 'Business unit')
        .map((f) => f.id)
        .filter((id): id is number => typeof id === 'number');

      const appliedAreas = otherFilters
        .filter((f) => f.section === 'Area')
        .map((f) => f.id)
        .filter((id): id is number => typeof id === 'number');

      if (appliedBUs.length > 0) {
        dispatch(fetchPCAreasAPI(appliedBUs));
        dispatch(fetchPCFieldsAPI({ businessUnitIds: appliedBUs, areaIds: appliedAreas }));
      } else if (appliedAreas.length > 0) {
        dispatch(fetchPCFieldsAPI({ businessUnitIds: [], areaIds: appliedAreas }));
      }
    }

    let newSelectedCheckboxes;
    if (PCFilter) {
      newSelectedCheckboxes = sectionItems.map((items, idx) => {
        const sectionTitle = filterSectionTitles[idx];
        const appliedForSection = otherFilters.filter((f) => f.section === sectionTitle);
        return (items as FilterItem[]).filter((item) => {
          if (typeof item === 'object') {
            return appliedForSection.some((f) => f.id === item.Id && f.value === item.Name);
          }
          return false;
        });
      });

      // Set PC selected assets from asset filters
      const selectedAssetIds = assetFilters.map((filter) => filter.id as string).filter(Boolean);
      if (selectedAssetIds.length > 0) {
        dispatch({ type: 'wellFilters/setPCSelectedAssets', payload: selectedAssetIds });
      }
    } else {
      newSelectedCheckboxes = sectionItems.map((items, idx) => {
        const section = filterSectionTitles[idx];
        return (items as FilterItem[]).filter((item: FilterItem) =>
          otherFilters.some((f) => f.section === section && f.value === item),
        );
      });
    }
    const currentState = {
      ...defaultState,
      selectedFilter: selectedFilterType,
      selectedCheckboxes: newSelectedCheckboxes,
      selectedGroups: groupFilters,
      isGroupsOpen: selectedFilterType === 'groups' && groupFilters.length > 0,
    };
    setInitialPanelState(currentState);
    setPanelState(currentState);
  }, [isWellFilterOpen, isCustomViews]);

  const handleCheckboxChange = (sectionIdx: number, item: any) => {
    const section = selectedCheckboxes[sectionIdx];
    let isChecked;
    if (PCFilter) {
      isChecked = section.some((i: any) => i.Name === item.Name && i.Id === item.Id);
    } else {
      isChecked = section.includes(item);
    }
    const updatedSection = isChecked
      ? section.filter((i: any) => (PCFilter ? !(i.Name === item.Name && i.Id === item.Id) : i !== item))
      : [...section, item];
    let updatedCheckboxes = selectedCheckboxes.map((s, idx) => (idx === sectionIdx ? updatedSection : s));

    if (PCFilter) {
      if (sectionIdx === 0) {
        const selectedBUIds = updatedSection
          .map((bu) => (typeof bu === 'object' ? bu.Id : null))
          .filter((id): id is number => id !== null);

        if (selectedBUIds.length > 0) {
          dispatch(fetchPCAreasAPI(selectedBUIds));
          dispatch(fetchPCFieldsAPI({ businessUnitIds: selectedBUIds, areaIds: [] }));
        } else {
          dispatch(fetchPCAreasAPI());
          dispatch(fetchPCFieldsAPI({}));
        }

        const validAreas = Array.isArray(pcAreaItems) ? pcAreaItems.map((area) => area.AreaId) : [];
        const validFields = Array.isArray(pcFieldItems) ? pcFieldItems.map((field) => field.FieldId) : [];

        const prevAreas = selectedCheckboxes[1].filter(
          (area) => typeof area === 'object' && validAreas.includes(area.Id),
        );
        const prevFields = selectedCheckboxes[2].filter(
          (field) => typeof field === 'object' && validFields.includes(field.Id),
        );

        updatedCheckboxes = [updatedSection, prevAreas, prevFields];
        updatePanelState({ selectedCheckboxes: updatedCheckboxes });
        return;
      }
      if (sectionIdx === 1) {
        const latestBUSelection = selectedCheckboxes[0];
        const selectedBUIds = latestBUSelection
          .map((bu) => (typeof bu === 'object' ? bu.Id : null))
          .filter((id): id is number => id !== null);
        const selectedAreaIds = updatedSection
          .map((area) => (typeof area === 'object' ? area.Id : null))
          .filter((id): id is number => id !== null);

        if (selectedBUIds.length > 0 || selectedAreaIds.length > 0) {
          dispatch(fetchPCFieldsAPI({ businessUnitIds: selectedBUIds, areaIds: selectedAreaIds }));
        } else {
          dispatch(fetchPCFieldsAPI({}));
        }

        const validFields = Array.isArray(pcFieldItems) ? pcFieldItems.map((field) => field.FieldId) : [];
        const prevFields = selectedCheckboxes[2].filter(
          (field) => typeof field === 'object' && validFields.includes(field.Id),
        );

        updatedCheckboxes = [latestBUSelection, updatedSection, prevFields];
        updatePanelState({ selectedCheckboxes: updatedCheckboxes });
        return;
      }
      if (sectionIdx === 2) {
        updatedCheckboxes = [selectedCheckboxes[0], selectedCheckboxes[1], updatedSection];
        updatePanelState({ selectedCheckboxes: updatedCheckboxes });
        return;
      }
    }
    updatePanelState({ selectedCheckboxes: updatedCheckboxes });
  };

  const toggleSection = (idx: number) => {
    const updatedSections = openSections.map((open, i) => (i === idx ? !open : open));
    updatePanelState({ openSections: updatedSections });
  };

  const toggleShowMore = (idx: number) => {
    const updatedSections = expandedSections.map((exp, i) => (i === idx ? !exp : exp));
    updatePanelState({ expandedSections: updatedSections });
  };

  const handleResetClick = () => {
    setPanelState(defaultState);
    setResetClicked(true);

    if (PCFilter) {
      dispatch({ type: 'wellFilters/setPCSelectedAssets', payload: [] });
      dispatch(fetchPCAreasAPI());
      dispatch(fetchPCFieldsAPI({}));
    }
  };

  const anyFiltersSelected =
    selectedCheckboxes.some((arr) => arr.length > 0) ||
    selectedGroups.length > 0 ||
    (PCFilter && pcSelectedAssets.length > 0);

  const hasUnsavedChanges =
    resetClicked || (JSON.stringify(panelState) !== JSON.stringify(initialPanelState) && anyFiltersSelected);

  const handlePanelClose = () => {
    if (hasUnsavedChanges) {
      setShowConfirmModal(true);
    } else {
      setIsWellFilterOpen(false);
      if (PCFilter && selectedFilters.length === 0) {
        dispatch(fetchPCAreasAPI());
        dispatch(fetchPCFieldsAPI({}));
      }
    }
  };

  const handleDiscardChanges = () => {
    setShowConfirmModal(false);
    setIsWellFilterOpen(false);
    setPanelState(initialPanelState);
    setResetClicked(false);

    if (PCFilter) {
      const { assetFilters, otherFilters } = separatedFilters;
      const originalAssetIds = assetFilters.map((filter) => filter.id as string).filter(Boolean);
      dispatch({ type: 'wellFilters/setPCSelectedAssets', payload: originalAssetIds });

      const appliedBUs = otherFilters
        .filter((f) => f.section === 'Business unit')
        .map((f) => f.id)
        .filter((id): id is number => typeof id === 'number');

      const appliedAreas = otherFilters
        .filter((f) => f.section === 'Area')
        .map((f) => f.id)
        .filter((id): id is number => typeof id === 'number');

      if (appliedBUs.length > 0) {
        dispatch(fetchPCAreasAPI(appliedBUs));
        dispatch(fetchPCFieldsAPI({ businessUnitIds: appliedBUs, areaIds: appliedAreas }));
      } else if (appliedAreas.length > 0) {
        dispatch(fetchPCFieldsAPI({ businessUnitIds: [], areaIds: appliedAreas }));
      } else {
        dispatch(fetchPCAreasAPI());
        dispatch(fetchPCFieldsAPI({}));
      }
    }
  };

  const buildPayloadFromPanel = (): {
    mops?: string;
    businessUnits?: string;
    fields?: string;
    groups?: string;
  } => {
    const businessUnitsCsv = arrToCleanArr(selectedCheckboxes[0]).join(',');
    const fieldsCsv = arrToCleanArr(selectedCheckboxes[1]).join(',');

    const rawMops = arrToCleanArr(selectedCheckboxes[2]);
    const mopsCsv = rawMops.join(',');

    const groupsCsv = arrToCleanArr(selectedGroups || []).join(',');

    return {
      mops: mopsCsv || undefined,
      businessUnits: businessUnitsCsv || undefined,
      fields: fieldsCsv || undefined,
      groups: groupsCsv || undefined,
    };
  };
  const buildPayloadFromPanelArr = (): DashboardFilterArr => {
    const businessUnitsCsv = arrToCleanArr(selectedCheckboxes[0]);
    const fieldsCsv = arrToCleanArr(selectedCheckboxes[1]);

    const rawMops = arrToCleanArr(selectedCheckboxes[2]);

    const groupsCsv = arrToCleanArr(selectedGroups || []);

    return {
      mops: rawMops || undefined,
      businessUnits: businessUnitsCsv || undefined,
      fields: fieldsCsv || undefined,
      groups: groupsCsv || undefined,
    };
  };

  const handleApply = () => {
    const allSelected: SectionFilter[] = [];
    let filteredCheckboxes = selectedCheckboxes;
    if (PCFilter) {
      const validAreaIds = Array.isArray(pcAreaItems) ? pcAreaItems.map((area) => area.AreaId) : [];
      const validFieldIds = Array.isArray(pcFieldItems) ? pcFieldItems.map((field) => field.FieldId) : [];
      filteredCheckboxes = [
        selectedCheckboxes[0],
        selectedCheckboxes[1].filter((area) => typeof area === 'object' && validAreaIds.includes(area.Id)),
        selectedCheckboxes[2].filter((field) => typeof field === 'object' && validFieldIds.includes(field.Id)),
      ];
    }
    filteredCheckboxes.forEach((sectionArr, idx) => {
      const section = filterSectionTitles[idx];
      sectionArr.forEach((item) => {
        if (PCFilter && typeof item === 'object') {
          allSelected.push({ section, value: item.Name, id: item.Id });
        } else if (!PCFilter && typeof item === 'string') {
          allSelected.push({ section, value: item });
        }
      });
    });
    const groupFilters: SectionFilter[] = selectedGroups.map((g) => ({ section: 'Groups', value: g }));

    const assetFilters: SectionFilter[] = [];
    if (PCFilter && pcSelectedAssets.length > 0) {
      const getAssetNames = (groups: any[]): { [assetId: string]: string } => {
        const assetIdToName: { [assetId: string]: string } = {};
        const collectAssets = (groupList: any[]) => {
          groupList.forEach((group) => {
            if (group.assets) {
              group.assets.forEach((asset: any) => {
                assetIdToName[asset.assetId] = asset.assetName;
              });
            }
            if (group.childGroups?.length) {
              collectAssets(group.childGroups);
            }
          });
        };
        collectAssets(groups);
        return assetIdToName;
      };

      const assetIdToName = getAssetNames(assetGroups);
      pcSelectedAssets.forEach((assetId) => {
        const assetName = assetIdToName[assetId];
        if (assetName) {
          assetFilters.push({ section: 'Assets', value: assetName, id: assetId });
        }
      });
    }

    const allFilters = [...allSelected, ...groupFilters, ...assetFilters];

    const filterTypeToStore: 'all' | 'groups' | 'watchlist' =
      selectedFilter === 'groups' && selectedGroups.length > 0
        ? 'groups'
        : selectedFilter === 'watchlist'
          ? 'watchlist'
          : 'all';

    if (PCFilter) {
      dispatch({ type: 'wellFilters/setPCSelectedFilters', payload: allFilters });
      dispatch({ type: 'wellFilters/setPCSelectedFilterType', payload: filterTypeToStore });

      if (pcSelectedAssets && pcSelectedAssets.length > 0) {
        dispatch(fetchPCWellDetailsThunk(pcSelectedAssets));
      }
    } else {
      dispatch(setSelectedFilters(allFilters));
      dispatch(setSelectedFilterType(filterTypeToStore));
    }

    if (isWellSummaryPage) {
      // Well Summary filtering logic
      const payload = buildPayloadFromPanel();
      const payloadArr = buildPayloadFromPanelArr();
      // Clear old status values immediately so UI doesn't show stale numbers
      dispatch(clearStatusValues());

      // fetch filtered dashboard, then request status counts using same filters
      dispatch(fetchFilteredWellSummary(payload as any))
        .unwrap()
        .then(() => {
          const baseStatusKeys = ['Active', 'Running', 'Shutdown', 'CommFail', 'Offline'];
          const statusKeys = fetchDataSummary ? [...baseStatusKeys, 'Restarted', 'Cycles', 'NoData'] : baseStatusKeys;

          // Use the new thunk that accepts filters so counts are derived from filtered dataset
          statusKeys.forEach((s) => dispatch(fetchStatusCountsWithFilters({ status: s, filters: payload as any })));

          // refresh field production in parallel
          dispatch(fetchFieldProduction(payload as any)).catch((e) => console.error('fetchFieldProduction error', e));
        })
        .catch((err) => {
          console.error('fetchFilteredWellSummary failed', err);
        });
      dispatch(fetchRodpumpclassification(payloadArr));
      //refresh alarms in parallel
      dispatch(fetchAlarmsData(payloadArr)).catch((e) => console.error('fetchAlarmsData error', e));
    }
    setPanelState(defaultState);
    setIsWellFilterOpen(false);
    setResetClicked(false);
  };

  const updatePanelState = useCallback((updates: Partial<typeof panelState>) => {
    setResetClicked(false);
    setPanelState((prev) => ({ ...prev, ...updates }));
  }, []);
  const applyDisabled = !(anyFiltersSelected || resetClicked);
  return (
    <>
      <SidePanel
        open={isWellFilterOpen}
        onClose={handlePanelClose}
        header='Well filters'
        width={'567px'}
        footer={true}
        handleResetClick={handleResetClick}
        handleApply={handleApply}
        applyDisabled={applyDisabled}
      >
        <div className='well-filter-panel-flex'>
          <div className='well-filter-content'>
            <div className='well-filter-wells'>
              <div className='well-filter-radio-header'>
                <label className='well-filter-radio-group'>
                  <input
                    type='radio'
                    name='well-filter'
                    value='all'
                    checked={selectedFilter === 'all'}
                    onChange={() => updatePanelState({ selectedFilter: 'all' })}
                    className='well-filter-radio-input'
                  />
                  <span className='well-filter-radio-custom'></span>
                  <span className='well-filter-radio-label'>All wells</span>
                </label>
              </div>

              <div className='well-filter-radio-group' style={{ width: '100%' }}>
                <div className='well-filter-radio-header'>
                  <Tooltip content='My watchlist coming soon' direction='right'>
                    <label className='well-filter-radio-label-disabled'>
                      <input
                        type='radio'
                        name='well-filter'
                        value='watchlist'
                        checked={selectedFilter === 'watchlist'}
                        onChange={() => updatePanelState({ selectedFilter: 'watchlist' })}
                        className='well-filter-radio-input'
                        disabled
                      />
                      <span className='well-filter-radio-custom'></span>
                      <span className='well-filter-radio-label'>My watchlist</span>
                    </label>
                  </Tooltip>
                  <ChevronDown className='well-filter-chevron' />
                </div>
              </div>

              <div className='well-filter-radio-group' style={{ width: '100%' }}>
                <div
                  className='well-filter-radio-header'
                  onClick={(e) => {
                    if ((e.target as HTMLElement).closest('label')) return;
                    const newIsOpen = !isGroupsOpen;
                    updatePanelState({ isGroupsOpen: newIsOpen, selectedFilter: 'groups' });
                  }}
                >
                  <label
                    onClick={(e) => {
                      e.stopPropagation();
                      updatePanelState({ selectedFilter: 'groups', isGroupsOpen: true });
                    }}
                  >
                    <input
                      type='radio'
                      name='well-filter'
                      value='groups'
                      checked={selectedFilter === 'groups'}
                      onChange={() => updatePanelState({ selectedFilter: 'groups', isGroupsOpen: true })}
                      className='well-filter-radio-input'
                    />
                    <span className='well-filter-radio-custom'></span>
                    <span className='well-filter-radio-label'>Groups</span>
                  </label>
                  {isGroupsOpen ? (
                    <ChevronUp className='well-filter-chevron' />
                  ) : (
                    <ChevronDown className='well-filter-chevron' />
                  )}
                </div>
              </div>
              {isGroupsOpen && (
                <WellGroupDropDown
                  onSelectedGroupsChange={(groups) => updatePanelState({ selectedGroups: groups })}
                  selectedGroups={selectedGroups}
                  PCFilter={!!PCFilter}
                />
              )}
            </div>
          </div>
        </div>
        <div className='well-filter-categories'>
          {filterSectionTitles.map((title, idx) => {
            let items, showAll, visibleItems, hasMore;
            if (PCFilter) {
              items = [...sectionItems[idx]].sort((a, b) => {
                if (typeof a === 'object' && typeof b === 'object') {
                  return a.Name.localeCompare(b.Name);
                }
                return 0;
              });
              showAll = expandedSections[idx];
              visibleItems = showAll ? items : items.slice(0, 4);
              hasMore = items.length > 4;
            } else {
              items = [...sectionItems[idx]].sort((a, b) => {
                if (typeof a === 'string' && typeof b === 'string') {
                  return a.localeCompare(b);
                }
                return 0;
              });
              showAll = expandedSections[idx];
              visibleItems = showAll ? items : items.slice(0, 4);
              hasMore = items.length > 4;
            }

            return (
              <div className='well-filter-category-section' key={title}>
                <div className={`well-filter-category-header `} onClick={() => toggleSection(idx)}>
                  <span className='well-filter-category-label'>{title}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span className='well-filter-badge'>{selectedCheckboxes[idx].length}</span>
                    {openSections[idx] ? (
                      <ChevronUp className='well-filter-chevron' />
                    ) : (
                      <ChevronDown className='well-filter-chevron' />
                    )}
                  </div>
                </div>

                {openSections[idx] && (
                  <div className='well-filter-category-items'>
                    {visibleItems.map((item) => (
                      <label
                        key={PCFilter && typeof item === 'object' ? item.Id : String(item)}
                        className='well-filter-checkbox-label'
                      >
                        <input
                          type='checkbox'
                          className='card-table-checkbox'
                          checked={
                            PCFilter && typeof item === 'object'
                              ? selectedCheckboxes[idx].some(
                                (i: FilterItem) => typeof i === 'object' && i.Name === item.Name && i.Id === item.Id,
                              )
                              : selectedCheckboxes[idx].includes(item)
                          }
                          onChange={() => handleCheckboxChange(idx, item)}
                        />
                        <span className='well-filter-checkbox-custom'></span>
                        <span className='well-filter-category-labels'>
                          {PCFilter && typeof item === 'object' ? item.Name : String(item)}
                        </span>
                      </label>
                    ))}
                    {hasMore && (
                      <button type='button' className='well-filter-show-more' onClick={() => toggleShowMore(idx)}>
                        {showAll ? 'Show less' : 'Show more'}
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </SidePanel>

      <Modal
        open={showConfirmModal}
        setOpen={setShowConfirmModal}
        callbackOnConfirm={handleDiscardChanges}
        title='Confirm navigation'
        message='You have unapplied changes that will be lost if you decide to continue. Are you sure you want to discard these changes?'
        buttonText='Yes'
        Icon={shutdownIcon}
        hideCancelButton={false}
        buttonColor='red'
      />
    </>
  );
};

export default WellFilterPanel;
